import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Calendar } from "../components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ClientePicker } from "../components/ClientePicker";
import { PageHeader } from "../components/PageHeader";
import { Plus, Trash, Clock, MapPin, CaretLeft, CaretRight, ListBullets } from "@phosphor-icons/react";
import { useNuovoParam } from "../lib/useNuovoParam";
import { ScadenzeExtra, FiltriFonti, FONTI } from "../components/calendario/FontiCalendario";
import { it } from "date-fns/locale";

// Utility: lunedì della settimana di d (00:00)
const startOfWeek = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const day = x.getDay(); // 0=dom
  const diff = day === 0 ? -6 : 1 - day;
  x.setDate(x.getDate() + diff);
  return x;
};
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const fmtLocalDT = (d) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const HOUR_START = 7;
const HOUR_END = 21; // esclusiva
const SLOT_MIN = 60; // minuti per riga
const ROW_HEIGHT = 56; // px per riga (h-14)
const DAYS_LABELS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

// Colore singolo per gli appuntamenti (indigo)
const APP_COLOR = { bg: "bg-indigo-100", bd: "border-indigo-500", text: "text-indigo-900" };
const eventColor = () => APP_COLOR;

export default function Calendario() {
  const [appuntamenti, setAppuntamenti] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [pratiche, setPratiche] = useState([]);
  const [anchorDate, setAnchorDate] = useState(new Date());
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("cal.view") || "month"); // week | month
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draggedId, setDraggedId] = useState(null);
  const [resizingId, setResizingId] = useState(null);
  const [sp] = useSearchParams();
  const filtroIniziale = sp.get("filtro");
  const [showApp, setShowApp] = useState(!filtroIniziale || filtroIniziale === "studio");
  const [showScad, setShowScad] = useState(!filtroIniziale || filtroIniziale === "studio");
  const [showImp, setShowImp] = useState(!filtroIniziale || filtroIniziale === "impresa");
  const [showPers, setShowPers] = useState(!filtroIniziale || filtroIniziale === "personali");
  const [scadImpresa, setScadImpresa] = useState([]);
  const [scadPers, setScadPers] = useState([]);
  const [form, setForm] = useState({
    titolo: "", data_ora: "", durata_minuti: 60, luogo: "",
    cliente_id: "", pratica_id: "", note: "",
  });

  const load = async () => {
    const [a, c, p] = await Promise.all([
      api.get("/appuntamenti"),
      api.get("/clienti"),
      api.get("/pratiche"),
    ]);
    setAppuntamenti(a.data);
    setClienti(c.data);
    setPratiche(p.data);
    api.get("/impresa/scadenzario?giorni=365").then((r) => setScadImpresa(r.data?.voci || [])).catch(() => {});
    api.get("/scadenze-professionali").then((r) => setScadPers(r.data?.items || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const extraPerGiorno = useMemo(() => ScadenzeExtra.perGiorno({ scadImpresa: showImp ? scadImpresa : [], scadPers: showPers ? scadPers : [] }), [scadImpresa, scadPers, showImp, showPers]);

  const resetForm = () => setForm({ titolo: "", data_ora: "", durata_minuti: 60, luogo: "", cliente_id: "", pratica_id: "", note: "" });

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        durata_minuti: parseInt(form.durata_minuti) || 60,
        cliente_id: form.cliente_id || null,
        pratica_id: form.pratica_id || null,
      };
      if (editingId) {
        await api.put(`/appuntamenti/${editingId}`, payload);
        toast.success("Appuntamento aggiornato");
      } else {
        await api.post("/appuntamenti", payload);
        toast.success("Appuntamento creato");
      }
      setOpen(false); setEditingId(null); resetForm(); load();
    } catch { toast.error("Errore salvataggio"); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare appuntamento?")) return;
    try { await api.delete(`/appuntamenti/${id}`); toast.success("Eliminato"); setOpen(false); load(); }
    catch { toast.error("Errore"); }
  };

  const openNew = (prefill = {}) => {
    setEditingId(null);
    resetForm();
    if (prefill.data_ora) setForm((f) => ({ ...f, ...prefill }));
    setOpen(true);
  };
  useNuovoParam(openNew);
  const openEdit = (a) => {
    setEditingId(a.id);
    setForm({
      titolo: a.titolo || "",
      data_ora: a.data_ora ? fmtLocalDT(new Date(a.data_ora)) : "",
      durata_minuti: a.durata_minuti || 60,
      luogo: a.luogo || "",
      cliente_id: a.cliente_id || "",
      pratica_id: a.pratica_id || "",
      note: a.note || "",
    });
    setOpen(true);
  };

  const clientName = (id) => {
    const c = clienti.find((x) => x.id === id);
    return c ? `${c.nome} ${c.cognome || ""}`.trim() : null;
  };
  const praticaTitle = (id) => pratiche.find((x) => x.id === id)?.titolo || null;

  // Settimana corrente
  const weekStart = useMemo(() => startOfWeek(anchorDate), [anchorDate]);
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const today = new Date();

  const weekLabel = useMemo(() => {
    const end = addDays(weekStart, 6);
    const sameMonth = weekStart.getMonth() === end.getMonth();
    if (sameMonth) {
      return `${weekStart.getDate()} – ${end.getDate()} ${end.toLocaleDateString("it-IT", { month: "long", year: "numeric" })}`;
    }
    return `${weekStart.toLocaleDateString("it-IT", { day: "numeric", month: "short" })} – ${end.toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" })}`;
  }, [weekStart]);

  // Eventi per giorno della settimana (filtrati per tipo)
  const eventsByDay = useMemo(() => {
    const map = weekDays.map(() => []);
    if (!showApp) return map;
    appuntamenti.forEach((a) => {
      if (!a.data_ora) return;
      const d = new Date(a.data_ora);
      const idx = weekDays.findIndex((wd) => sameDay(wd, d));
      if (idx >= 0) map[idx].push(a);
    });
    return map;
  }, [appuntamenti, weekDays, showApp]);

  const goPrev = () => setAnchorDate(addDays(weekStart, viewMode === "week" ? -7 : -30));
  const goNext = () => setAnchorDate(addDays(weekStart, viewMode === "week" ? 7 : 30));
  const goToday = () => setAnchorDate(new Date());

  const hours = useMemo(() => Array.from({ length: HOUR_END - HOUR_START }, (_, i) => HOUR_START + i), []);

  // Click slot vuoto
  const handleSlotClick = (day, hour) => {
    const d = new Date(day);
    d.setHours(hour, 0, 0, 0);
    openNew({ data_ora: fmtLocalDT(d), durata_minuti: 60 });
  };

  // ---- Drag & Drop (spostamento evento) ----
  const handleDragStart = (e, appuntamento) => {
    setDraggedId(appuntamento.id);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", appuntamento.id);
  };
  const handleDragEnd = () => setDraggedId(null);
  const handleSlotDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const handleSlotDrop = async (e, day, hour) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain") || draggedId;
    setDraggedId(null);
    if (!id) return;
    const a = appuntamenti.find((x) => x.id === id);
    if (!a) return;
    // Calcola nuovi minuti sulla base della Y all'interno dello slot (snap ai 15 min)
    const target = e.currentTarget.getBoundingClientRect();
    const offY = e.clientY - target.top;
    const extraMinutes = Math.round((offY / ROW_HEIGHT) * SLOT_MIN / 15) * 15;
    const newDate = new Date(day);
    newDate.setHours(hour, extraMinutes, 0, 0);
    const oldDate = new Date(a.data_ora);
    if (sameDay(newDate, oldDate) && newDate.getHours() === oldDate.getHours() && newDate.getMinutes() === oldDate.getMinutes()) return;
    // Ottimistico
    setAppuntamenti((prev) => prev.map((x) => x.id === id ? { ...x, data_ora: newDate.toISOString() } : x));
    try {
      await api.put(`/appuntamenti/${id}`, {
        titolo: a.titolo, data_ora: newDate.toISOString(), durata_minuti: a.durata_minuti,
        luogo: a.luogo || "", cliente_id: a.cliente_id || null, pratica_id: a.pratica_id || null, note: a.note || "",
      });
      toast.success(`Spostato al ${newDate.toLocaleDateString("it-IT")} ${newDate.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}`);
    } catch { toast.error("Errore spostamento"); load(); }
  };

  // ---- Resize durata evento (pointer events sul bordo inferiore) ----
  const startResize = (e, a) => {
    e.stopPropagation();
    e.preventDefault();
    setResizingId(a.id);
    const startY = e.clientY;
    const startDuration = a.durata_minuti || 60;
    const move = (mv) => {
      const dy = mv.clientY - startY;
      const deltaMin = Math.round((dy / ROW_HEIGHT) * SLOT_MIN / 15) * 15;
      const newDur = Math.max(15, startDuration + deltaMin);
      setAppuntamenti((prev) => prev.map((x) => x.id === a.id ? { ...x, durata_minuti: newDur } : x));
    };
    const up = async (upEv) => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      const dy = upEv.clientY - startY;
      const deltaMin = Math.round((dy / ROW_HEIGHT) * SLOT_MIN / 15) * 15;
      const newDur = Math.max(15, startDuration + deltaMin);
      setResizingId(null);
      if (newDur === startDuration) return;
      try {
        await api.put(`/appuntamenti/${a.id}`, {
          titolo: a.titolo, data_ora: a.data_ora, durata_minuti: newDur,
          luogo: a.luogo || "", cliente_id: a.cliente_id || null, pratica_id: a.pratica_id || null, note: a.note || "",
        });
        toast.success(`Durata: ${newDur} min`);
      } catch { toast.error("Errore ridimensiona"); load(); }
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  // Posizionamento evento in griglia (top + height)
  const eventGeometry = (a) => {
    const d = new Date(a.data_ora);
    const startMin = d.getHours() * 60 + d.getMinutes();
    const startOffset = (startMin - HOUR_START * 60);
    const durMin = a.durata_minuti || 60;
    const top = (startOffset / SLOT_MIN) * ROW_HEIGHT;
    const height = Math.max(20, (durMin / SLOT_MIN) * ROW_HEIGHT - 3);
    return { top, height };
  };

  // Marker "ora attuale"
  const nowMarker = useMemo(() => {
    const now = new Date();
    const idx = weekDays.findIndex((wd) => sameDay(wd, now));
    if (idx < 0) return null;
    const min = now.getHours() * 60 + now.getMinutes();
    if (min < HOUR_START * 60 || min > HOUR_END * 60) return null;
    return { idx, top: ((min - HOUR_START * 60) / SLOT_MIN) * ROW_HEIGHT };
  }, [weekDays]);

  // Overview: eventi settimana totali
  const totWeekEvents = eventsByDay.reduce((s, arr) => s + arr.length, 0);

  return (
    <div className="p-6 md:p-8 w-full max-w-[1600px] mx-auto" data-testid="calendario-page">
      <PageHeader
        eyebrow="Agenda"
        title="Calendario"
        description="Appuntamenti, scadenze degli incarichi, scadenze dell'impresa e personali: tutto in un posto."
        actions={<>
          {showImp && <Button asChild variant="outline" data-testid="cal-elenco-impresa"><Link to="/impresa/scadenzario"><ListBullets size={16} /> Elenco impresa</Link></Button>}
          {showPers && <Button asChild variant="outline" data-testid="cal-gestisci-personali"><Link to="/scadenze-professionali"><ListBullets size={16} /> Le mie scadenze</Link></Button>}
          <Button onClick={() => openNew()} data-testid="new-appuntamento-button"><Plus size={16} weight="bold" /> Nuovo appuntamento</Button>
        </>}
      />

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={goToday} data-testid="cal-today">Oggi</Button>
          <div className="flex">
            <button onClick={goPrev} className="border border-slate-200 rounded-l-md px-2 py-1.5 hover:bg-slate-100" data-testid="cal-prev" title="Precedente"><CaretLeft size={14} /></button>
            <button onClick={goNext} className="border border-l-0 border-slate-200 rounded-r-md px-2 py-1.5 hover:bg-slate-100" data-testid="cal-next" title="Successivo"><CaretRight size={14} /></button>
          </div>
          <h2 className="font-heading font-semibold text-lg capitalize ml-2" data-testid="cal-label">{viewMode === "week" ? `Settimana · ${weekLabel}` : `${anchorDate.toLocaleDateString("it-IT", { month: "long", year: "numeric" })}`}</h2>
        </div>
        <div className="flex items-center gap-3">
          {/* Filtri tipo evento */}
          <div className="inline-flex items-center bg-white border border-slate-200 rounded-md p-0.5" data-testid="event-filter">
            <button
              onClick={() => setShowApp((v) => !v)}
              className={`px-2 py-1 rounded text-[11px] font-medium inline-flex items-center gap-1.5 ${showApp ? "bg-indigo-100 text-indigo-800 border border-indigo-300" : "text-slate-400 hover:text-slate-700"}`}
              data-testid="filter-app"
              title="Mostra/nascondi appuntamenti"
            >
              <span className={`w-2 h-2 rounded-sm ${showApp ? "bg-indigo-500" : "bg-slate-300"}`} />
              Appuntamenti
            </button>
            <button
              onClick={() => setShowScad((v) => !v)}
              className={`px-2 py-1 rounded text-[11px] font-medium inline-flex items-center gap-1.5 ${showScad ? "bg-red-100 text-red-800 border border-red-300" : "text-slate-400 hover:text-slate-700"}`}
              data-testid="filter-scad"
              title="Mostra/nascondi scadenze"
            >
              <span className={`w-2 h-2 rounded-sm ${showScad ? "bg-red-500" : "bg-slate-300"}`} />
              Incarichi
            </button>
            <FiltriFonti showImp={showImp} setShowImp={setShowImp} showPers={showPers} setShowPers={setShowPers} />
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{totWeekEvents} eventi settimana</div>
          <div className="inline-flex bg-slate-100 rounded-md p-0.5">
            <button onClick={() => { setViewMode("week"); try { localStorage.setItem("cal.view", "week"); } catch { /* ignore */ } }} className={`px-3 py-1 rounded text-xs font-medium ${viewMode === "week" ? "bg-white shadow-sm" : "text-slate-600"}`} data-testid="view-week">Settimana</button>
            <button onClick={() => { setViewMode("month"); try { localStorage.setItem("cal.view", "month"); } catch { /* ignore */ } }} className={`px-3 py-1 rounded text-xs font-medium ${viewMode === "month" ? "bg-white shadow-sm" : "text-slate-600"}`} data-testid="view-month">Mese</button>
          </div>
        </div>
      </div>

      {viewMode === "week" ? (
        <Card className="p-0 border-slate-200 shadow-none overflow-hidden" data-testid="week-view">
          {/* Header giorni */}
          <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-slate-200 bg-slate-50 sticky top-0 z-10">
            <div className="p-2" />
            {weekDays.map((d, i) => {
              const isToday = sameDay(d, today);
              return (
                <button
                  key={i}
                  onClick={() => setAnchorDate(d)}
                  className={`p-2 text-center border-l border-slate-200 hover:bg-slate-100 ${isToday ? "bg-blue-50" : ""}`}
                  data-testid={`cal-day-header-${i}`}
                >
                  <div className={`text-[10px] font-mono uppercase tracking-wider ${isToday ? "text-blue-700" : "text-slate-500"}`}>{DAYS_LABELS[i]}</div>
                  <div className={`text-lg font-heading font-bold ${isToday ? "text-blue-700" : "text-slate-900"}`}>{d.getDate()}</div>
                  <ScadenzeExtra items={extraPerGiorno.get(ScadenzeExtra.key(d)) || []} compatto />
                </button>
              );
            })}
          </div>

          {/* Griglia oraria */}
          <div className="grid grid-cols-[60px_repeat(7,1fr)] relative">
            {/* Colonna ore */}
            <div className="border-r border-slate-200">
              {hours.map((h) => (
                <div key={h} className="h-14 text-[10px] font-mono text-slate-400 pr-1 text-right border-b border-slate-100">
                  <span className="relative -top-1.5 bg-white px-1">{String(h).padStart(2, "0")}:00</span>
                </div>
              ))}
            </div>
            {/* Colonne giorni */}
            {weekDays.map((day, dIdx) => (
              <div key={dIdx} className="relative border-l border-slate-200" data-testid={`cal-day-col-${dIdx}`}>
                {/* Righe cliccabili */}
                {hours.map((h) => (
                  <button
                    key={h}
                    onClick={() => handleSlotClick(day, h)}
                    onDragOver={handleSlotDragOver}
                    onDrop={(e) => handleSlotDrop(e, day, h)}
                    className="w-full h-14 border-b border-slate-100 hover:bg-blue-50/40 transition-colors block"
                    aria-label={`Nuovo appuntamento ${DAYS_LABELS[dIdx]} ${h}:00`}
                    data-testid={`cal-slot-${dIdx}-${h}`}
                  />
                ))}
                {/* Now marker */}
                {nowMarker && nowMarker.idx === dIdx && (
                  <div className="absolute left-0 right-0 pointer-events-none z-20" style={{ top: nowMarker.top }} data-testid="cal-now-marker">
                    <div className="h-0.5 bg-red-500" />
                    <div className="absolute -left-1.5 -top-1 w-3 h-3 rounded-full bg-red-500" />
                  </div>
                )}
                {/* Eventi */}
                {eventsByDay[dIdx].map((a) => {
                  const g = eventGeometry(a);
                  const c = eventColor(a.titolo);
                  const start = new Date(a.data_ora);
                  const end = new Date(start.getTime() + (a.durata_minuti || 60) * 60000);
                  const startStr = start.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
                  const endStr = end.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
                  const isDragging = draggedId === a.id;
                  const isResizing = resizingId === a.id;
                  return (
                    <div
                      key={a.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, a)}
                      onDragEnd={handleDragEnd}
                      onClick={(e) => { e.stopPropagation(); if (!isResizing) openEdit(a); }}
                      className={`absolute left-1 right-1 border-l-4 rounded-md px-2 py-1 text-left overflow-hidden group cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow ${c.bg} ${c.bd} ${c.text} ${isDragging ? "opacity-40" : ""} ${isResizing ? "ring-2 ring-blue-400" : ""}`}
                      style={{ top: g.top, height: g.height }}
                      data-testid={`cal-event-${a.id}`}
                      title={`${a.titolo} · trascina per spostare · trascina il bordo inferiore per allungare`}
                    >
                      <div className="text-[11px] font-mono opacity-70">{startStr} – {endStr}</div>
                      <div className="text-xs font-medium truncate">{a.titolo}</div>
                      {a.luogo && g.height > 40 && <div className="text-[10px] flex items-center gap-0.5 opacity-70 truncate"><MapPin size={9} /> {a.luogo}</div>}
                      {/* Handle resize */}
                      <div
                        onPointerDown={(e) => startResize(e, a)}
                        onClick={(e) => e.stopPropagation()}
                        className="absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize opacity-0 group-hover:opacity-100 flex items-center justify-center"
                        data-testid={`cal-event-resize-${a.id}`}
                        title="Trascina per cambiare durata"
                      >
                        <div className={`h-1 w-8 rounded-full bg-current opacity-60`} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </Card>
      ) : (
        (() => {
          // Griglia mensile Google-Calendar-style
          const first = new Date(anchorDate.getFullYear(), anchorDate.getMonth(), 1);
          const startGrid = startOfWeek(first);
          // 6 righe × 7 giorni = 42 celle
          const days = Array.from({ length: 42 }, (_, i) => addDays(startGrid, i));
          const monthIdx = anchorDate.getMonth();
          // Aggregazione eventi per giorno
          const dayEvents = new Map(); // key = YYYY-MM-DD → [{type, ...}]
          const keyFor = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
          appuntamenti.forEach((a) => {
            if (!showApp) return;
            if (!a.data_ora) return;
            const d = new Date(a.data_ora);
            const k = keyFor(d);
            if (!dayEvents.has(k)) dayEvents.set(k, []);
            dayEvents.get(k).push({ type: "app", data: a, sort: d.getHours() * 60 + d.getMinutes() });
          });
          pratiche.forEach((p) => {
            if (!showScad) return;
            if (!p.scadenza) return;
            const d = new Date(p.scadenza);
            const k = keyFor(d);
            if (!dayEvents.has(k)) dayEvents.set(k, []);
            dayEvents.get(k).push({ type: "scad", data: p, sort: 9999 });
          });
          extraPerGiorno.forEach((arr, k) => { if (!dayEvents.has(k)) dayEvents.set(k, []); arr.forEach((x) => dayEvents.get(k).push({ type: "extra", data: x, sort: 9998 })); });
          dayEvents.forEach((arr) => arr.sort((a, b) => a.sort - b.sort));

          return (
            <Card className="p-0 border-slate-200 shadow-none overflow-hidden" data-testid="month-view">
              {/* Header giorni */}
              <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
                {DAYS_LABELS.map((l) => (
                  <div key={l} className="p-2 text-center text-[10px] font-mono uppercase tracking-widest text-slate-500 border-l border-slate-200 first:border-l-0">{l}</div>
                ))}
              </div>
              {/* Griglia mese */}
              <div className="grid grid-cols-7 auto-rows-fr" style={{ minHeight: "600px" }}>
                {days.map((d, i) => {
                  const isCurrentMonth = d.getMonth() === monthIdx;
                  const isToday = sameDay(d, new Date());
                  const evs = dayEvents.get(keyFor(d)) || [];
                  const shown = evs.slice(0, 3);
                  const more = evs.length - shown.length;
                  return (
                    <div
                      key={i}
                      onClick={() => { const nd = new Date(d); nd.setHours(9, 0, 0, 0); openNew({ data_ora: fmtLocalDT(nd), durata_minuti: 60 }); }}
                      className={`border-l border-b border-slate-100 p-1.5 relative min-h-[100px] cursor-pointer hover:bg-blue-50/40 transition-colors ${isCurrentMonth ? "bg-white" : "bg-slate-50/60 text-slate-400"} ${isToday ? "ring-2 ring-inset ring-blue-400" : ""}`}
                      data-testid={`month-cell-${keyFor(d)}`}
                    >
                      <div className={`text-xs font-mono mb-1 ${isToday ? "text-blue-700 font-bold" : isCurrentMonth ? "text-slate-700" : "text-slate-400"}`}>{d.getDate()}</div>
                      <div className="space-y-0.5">
                        {shown.map((ev, idx) => {
                          if (ev.type === "app") {
                            const c = eventColor(ev.data.titolo);
                            const time = new Date(ev.data.data_ora).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
                            return (
                              <button
                                key={`app-${ev.data.id}-${idx}`}
                                onClick={(e) => { e.stopPropagation(); openEdit(ev.data); }}
                                className={`w-full text-left rounded px-1.5 py-0.5 text-[10px] truncate ${c.bg} ${c.text} border-l-2 ${c.bd} hover:brightness-95`}
                                data-testid={`month-event-${ev.data.id}`}
                                title={`${time} ${ev.data.titolo}`}
                              >
                                <span className="font-mono">{time}</span> {ev.data.titolo}
                              </button>
                            );
                          }
                          if (ev.type === "extra") return <ScadenzeExtra key={`x-${idx}`} items={[ev.data]} />;
                          // scadenza pratica
                          const p = ev.data;
                          const cli = clienti.find((c) => c.id === p.cliente_id);
                          const cliName = cli ? (cli.ragione_sociale || `${cli.cognome || ""} ${cli.nome || ""}`.trim()) : "";
                          return (
                            <Link
                              key={`scad-${p.id}-${idx}`}
                              to={`/pratiche/${p.id}`}
                              onClick={(e) => e.stopPropagation()}
                              className="w-full text-left rounded px-1.5 py-0.5 text-[10px] truncate bg-red-50 text-red-800 border-l-2 border-red-500 hover:bg-red-100 block"
                              data-testid={`month-scadenza-${p.id}`}
                              title={`Scadenza ${p.titolo || ""} — ${cliName}`}
                            >
                              <span className="font-mono">⏱</span> {cliName || p.titolo}
                            </Link>
                          );
                        })}
                        {more > 0 && (
                          <button onClick={(e) => { e.stopPropagation(); setAnchorDate(d); setViewMode("week"); }} className="text-[10px] text-slate-500 hover:text-blue-600 font-mono" data-testid={`month-more-${keyFor(d)}`}>+ {more} altri</button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Legenda */}
              <div className="border-t border-slate-200 bg-slate-50 px-3 py-1.5 flex items-center justify-end gap-4 text-[10px] font-mono text-slate-500">
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-2 rounded-sm bg-blue-100 border-l-2 border-blue-500" /> Appuntamento</span>
                {showImp && <span className="flex items-center gap-1"><span className={`inline-block w-3 h-2 rounded-sm ${FONTI.impresa.legenda}`} /> Impresa</span>}
                {showPers && <span className="flex items-center gap-1"><span className={`inline-block w-3 h-2 rounded-sm ${FONTI.personali.legenda}`} /> Personale</span>}
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-2 rounded-sm bg-red-50 border-l-2 border-red-500" /> Scadenza incarico</span>
              </div>
            </Card>
          );
        })()
      )}

      {/* Dialog Nuovo/Modifica */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); resetForm(); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingId ? "Modifica appuntamento" : "Nuovo appuntamento"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2"><Label>Titolo</Label><Input value={form.titolo} onChange={(e) => setForm({ ...form, titolo: e.target.value })} required data-testid="app-titolo-input" placeholder="Es. Sopralluogo cantiere Rossi" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Data e ora</Label><Input type="datetime-local" value={form.data_ora} onChange={(e) => setForm({ ...form, data_ora: e.target.value })} required data-testid="app-dataora-input" /></div>
              <div className="space-y-2"><Label>Durata (min)</Label><Input type="number" value={form.durata_minuti} onChange={(e) => setForm({ ...form, durata_minuti: e.target.value })} step={15} /></div>
            </div>
            <div className="space-y-2"><Label>Luogo</Label><Input value={form.luogo} onChange={(e) => setForm({ ...form, luogo: e.target.value })} placeholder="Es. Via Roma 12, Firenze" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Cliente</Label>
                <ClientePicker clienti={clienti} value={form.cliente_id} onChange={(v) => setForm({ ...form, cliente_id: v })} onCreato={(c) => setClienti((l) => [...l, c])} testId="evento-cliente-picker" />
              </div>
              <div className="space-y-2">
                <Label>Pratica</Label>
                <Select value={form.pratica_id || "none"} onValueChange={(v) => setForm({ ...form, pratica_id: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Pratica" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Nessuna —</SelectItem>
                    {pratiche.map((p) => <SelectItem key={p.id} value={p.id}>{p.titolo}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2"><Label>Note</Label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} /></div>
            {form.cliente_id && <div className="text-xs text-slate-500">Cliente: {clientName(form.cliente_id)}</div>}
            {form.pratica_id && <div className="text-xs text-slate-500">Pratica: {praticaTitle(form.pratica_id)}</div>}
            <DialogFooter className="justify-between">
              {editingId ? (
                <Button type="button" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => del(editingId)} data-testid="app-delete-button"><Trash size={12} /> Elimina</Button>
              ) : <span />}
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
                <Button type="submit" data-testid="app-submit-button">{editingId ? "Aggiorna" : "Salva"}</Button>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
